import React, { useState } from 'react';
import { parseMagnetURI } from '../lib/sanitize';
import { getActiveTorrentFile, getWebTorrentClient } from '../lib/torrent';
import { saveFile } from '../lib/fileStorage';
import { COLORS } from '../styles/theme';

interface MediaAttachmentProps {
  magnetURI: string;
  attachmentType?: 'image' | 'audio' | 'video';
  thumbnailDataUrl?: string;
}

export function MediaAttachment({ magnetURI, attachmentType, thumbnailDataUrl }: MediaAttachmentProps) {
  const [blobURL, setBlobURL] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);

  const parsed = parseMagnetURI(magnetURI);
  const displayName = parsed?.displayName || 'media';
  const isImage = attachmentType === 'image';
  const isVideo = attachmentType === 'video' || /\.(mp4|webm|mov|mkv|m4v)$/i.test(displayName);

  function applyBlob(file: any) {
    function persist(blob: Blob) {
      saveFile(parsed!.infoHash, blob, displayName).catch(() => {});
      setBlobURL(URL.createObjectURL(blob));
      setLoading(false);
    }
    if (typeof file.getBlob === 'function') {
      const result = file.getBlob((err: any, blob: Blob) => {
        if (!err && blob) persist(blob); else setLoading(false);
      });
      if (result && typeof result.then === 'function') {
        result.then(persist).catch(() => setLoading(false));
      }
    } else if (typeof file.getBlobURL === 'function') {
      file.getBlobURL((err: any, url: string) => {
        if (!err && url) setBlobURL(url);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }

  function startDownload() {
    if (blobURL || loading || !parsed) return;
    setLoading(true);
    setProgress(0);
    const local = getActiveTorrentFile(parsed.infoHash);
    if (local) { applyBlob(local); return; }
    const client = getWebTorrentClient();
    client.add(parsed.raw, (torrent: any) => {
      if (!torrent.files?.length) { setLoading(false); return; }
      const file = torrent.files[0];
      if (torrent.done) { applyBlob(file); return; }
      const iv = setInterval(() => setProgress(Math.round(torrent.progress * 100)), 500);
      torrent.on('done', () => { clearInterval(iv); setProgress(100); applyBlob(file); });
    });
  }

  React.useEffect(() => {
    if (isImage && !blobURL && !loading) startDownload();
  }, []); // eslint-disable-line

  if (isImage) {
    if (blobURL) {
      return <img src={blobURL} alt="" style={{ maxWidth: '100%', maxHeight: 300, display: 'block', marginTop: 4, cursor: 'pointer' }} onClick={() => window.open(blobURL, '_blank')} />;
    }
    return (
      <div style={{ fontSize: '11px', fontFamily: 'monospace', color: COLORS.textMuted, marginTop: 2 }}>
        {loading ? (progress !== null && progress < 100 ? `image ${progress}%` : 'loading…') : '📷 image'}
      </div>
    );
  }

  if (isVideo) {
    if (blobURL && expanded) {
      return <video src={blobURL} controls autoPlay style={{ maxWidth: '100%', display: 'block', marginTop: 4, backgroundColor: '#000' }} />;
    }
    return (
      <div
        style={{ marginTop: 4, border: `1px solid ${COLORS.border}`, background: COLORS.panel, display: 'flex', alignItems: 'center', gap: 8, padding: 6, cursor: 'pointer' }}
        onClick={() => { if (!blobURL) startDownload(); setExpanded(true); }}
      >
        <div style={{ width: 64, height: 40, background: '#000', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          {thumbnailDataUrl
            ? <img src={thumbnailDataUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ color: COLORS.textMuted, fontSize: 16 }}>▶</span>}
        </div>
        <div style={{ fontFamily: 'monospace', fontSize: '11px', color: COLORS.text, minWidth: 0 }}>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: COLORS.accent }}>{displayName}</div>
          <div style={{ color: COLORS.textMuted, fontSize: '10px' }}>
            {loading ? (progress !== null && progress < 100 ? `${progress}%` : 'loading…') : '▶ play'}
          </div>
        </div>
      </div>
    );
  }

  if (blobURL) {
    return (
      <a href={blobURL} target="_blank" rel="noopener noreferrer"
        style={{ fontSize: '11px', fontFamily: 'monospace', color: COLORS.accent, display: 'block', marginTop: 2 }}>
        📎 {displayName} — open
      </a>
    );
  }

  return (
    <button onClick={startDownload} disabled={loading}
      style={{ background: 'transparent', border: `1px solid ${COLORS.border}`, color: COLORS.accent, cursor: loading ? 'default' : 'pointer', fontSize: '11px', fontFamily: 'monospace', padding: '2px 6px', marginTop: 2 }}>
      {loading ? (progress !== null && progress < 100 ? `downloading… ${progress}%` : 'loading…') : `📎 ${displayName} — view`}
    </button>
  );
}
